// Runtime, lazy face-eye detector backed by MediaPipe Tasks-Vision FaceDetector.
//
// CRITICAL (build-graph trap, see SPECS/widget-chamber-live-selfie.md):
// the detector runtime + wasm + model must NOT enter Vite's module graph,
// or `inlineDynamicImports: true` would inline ~10 MB into embed.js. We load
// the ESM bundle at RUNTIME from OUR OWN origin via a vite-ignored dynamic
// import (the URL is a runtime value), and point FilesetResolver at our own
// wasm dir — never MediaPipe's default jsDelivr CDN. `import type` below is
// erased at compile time and never bundled.
//
// Everything here is cosmetic and NON-BLOCKING: every failure (load error,
// timeout, CSP block, no face) is swallowed and surfaces as `null`, which the
// caller maps to a graceful fallback rung.

import type { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';
import type { Point } from './faceAlign';

interface VisionModule {
  FilesetResolver: typeof FilesetResolver;
  FaceDetector: typeof FaceDetector;
}

export interface EyeDetection {
  /** image-left eye center (smaller x), in image px */
  left: Point;
  /** image-right eye center (larger x), in image px */
  right: Point;
}

const MODEL_LOAD_TIMEOUT_MS = 9000;

let detectorPromise: Promise<FaceDetector> | null = null;

// Hide the specifier from the bundler entirely: a runtime variable + the
// @vite-ignore hint keeps this dynamic import external (never code-split or
// inlined into embed.js).
function importVisionModule(jsUrl: string): Promise<VisionModule> {
  return import(/* @vite-ignore */ jsUrl) as Promise<VisionModule>;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('detector timeout')), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

function createDetector(assetBase: string): Promise<FaceDetector> {
  return (async () => {
    const vision = await importVisionModule(`${assetBase}vision_bundle.mjs`);
    const fileset = await vision.FilesetResolver.forVisionTasks(`${assetBase}wasm`);
    return vision.FaceDetector.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: `${assetBase}blaze_face_short_range.tflite` },
      runningMode: 'IMAGE',
      minDetectionConfidence: 0.5,
    });
  })();
}

/**
 * Warm the detector (module + wasm + model) without blocking. Safe to call
 * repeatedly; the load is memoized. Intended to run when the user ENTERS the
 * photo step so Stage 4 has a warm detector.
 */
export function prefetchDetector(assetBase: string): void {
  if (!detectorPromise) {
    detectorPromise = withTimeout(createDetector(assetBase), MODEL_LOAD_TIMEOUT_MS);
  }
  // Swallow rejection so an unhandled prefetch error never bubbles.
  detectorPromise.catch(() => {
    detectorPromise = null;
  });
}

/**
 * Detect the two eye centers (in image px) for the first face. Returns null on
 * any failure (no face, load/timeout/CSP error). Never throws.
 */
export async function detectEyes(
  bitmap: ImageBitmap,
  assetBase: string,
): Promise<EyeDetection | null> {
  try {
    if (!detectorPromise) {
      detectorPromise = withTimeout(createDetector(assetBase), MODEL_LOAD_TIMEOUT_MS);
    }
    const detector = await detectorPromise;
    const result = detector.detect(bitmap);
    const kps = result?.detections?.[0]?.keypoints;
    if (!kps || kps.length < 2) return null;

    // BlazeFace short-range keypoints[0] = right eye, [1] = left eye, normalized
    // to [0,1] of the image. Assign by x so canonical left/right is unambiguous.
    const a: Point = { x: kps[0].x * bitmap.width, y: kps[0].y * bitmap.height };
    const b: Point = { x: kps[1].x * bitmap.width, y: kps[1].y * bitmap.height };
    const [left, right] = a.x <= b.x ? [a, b] : [b, a];
    if (!isFinite(left.x) || !isFinite(right.x)) return null;
    return { left, right };
  } catch {
    // Reset so a later attempt can retry a transient failure.
    detectorPromise = null;
    return null;
  }
}

/** Release the detector instance (called on Stage 4 unmount). */
export function disposeDetector(): void {
  const p = detectorPromise;
  detectorPromise = null;
  if (p) {
    p.then((d) => d.close()).catch(() => {});
  }
}
